import { onShowConfirmAlert } from "@dinesapp/dines-ui/utils/alerts";
import type { AxiosError } from "axios";
import { ApiErrorCodes } from "@/types/api/api";
import { isString } from "@/utils/utility";

/**
 * Title shown on the PIN-protection block alert.
 */
export const PIN_PROTECTION_TITLE = "PIN protection is turned on";

/**
 * User-facing message shown when an action is blocked by venue PIN protection.
 * Generic (no per-feature wording) because the interceptor only sees the request
 * URL, and carries no venue name or link because it runs outside React.
 */
export const PIN_PROTECTION_MESSAGE =
  "This action is blocked because PIN protection is turned on for this venue. To continue, turn it off in the Dines Dashboard under Settings > Team > PIN Protection.";

interface IPinProtectionErrorBody {
  data: { code: string };
}

const isPinProtectionErrorBody = (
  value: unknown,
): value is IPinProtectionErrorBody => {
  if (typeof value !== "object" || value === null || !("data" in value)) {
    return false;
  }
  const inner: unknown = value.data;
  return (
    typeof inner === "object" &&
    inner !== null &&
    "code" in inner &&
    isString(inner.code)
  );
};

/**
 * Returns true for the value APIService throws to callers on a PIN-protection
 * block. APIService rethrows `err.response.data` (the proxy body), so the code
 * sits at `error.data.code` and the HTTP status is not available here. Used to
 * stop TanStack Query retrying a permission error that can never succeed.
 */
export const isPinProtectionThrownError = (error: unknown): boolean =>
  isPinProtectionErrorBody(error) &&
  error.data.code === ApiErrorCodes.PinProtectionRequired;

/**
 * Returns true when an axios error is a venue PIN-protection block: HTTP 403
 * plus the thrown-body check above. In the interceptor the code sits at
 * error.response.data.data.code because the Next proxy wraps the core body
 * under `data` (see the core error nesting reference).
 */
export const isPinProtectionError = (error: AxiosError): boolean =>
  error.response?.status === 403 &&
  isPinProtectionThrownError(error.response.data);

/**
 * The dines-ui confirm alert mounts into a single shared `#confirm` container
 * and removes it on close. Checking that container lets us skip re-showing the
 * alert while it is already open, so TanStack Query retries do not re-mount or
 * stack it. The guard clears itself whenever the alert closes (including via
 * the close icon), so it can never get stuck.
 */
const isConfirmAlertOpen = () =>
  typeof document !== "undefined" &&
  document.getElementById("confirm") !== null;

/**
 * Shows the PIN-protection block as a persistent confirm dialog with a single
 * acknowledge button. Unlike a toast it stays until acknowledged, which suits a
 * block that is not a routine error.
 */
const showPinProtectionAlert = () => {
  if (isConfirmAlertOpen()) {
    return;
  }

  onShowConfirmAlert({
    labels: {
      id: "pin-protection-alert",
      title: PIN_PROTECTION_TITLE,
      desc: PIN_PROTECTION_MESSAGE,
      yesLabel: "Got it",
    },
    visibilityOptions: {
      showYesButton: true,
      showNoButton: false,
      showCancelButton: false,
      showDescription: true,
    },
  });
};

/**
 * Axios response-error interceptor. On a PIN-protection block it shows a
 * persistent confirm alert, then re-rejects the error unchanged so downstream
 * handlers (loading state, mutation onError, existing catches) behave as
 * before. Guarded by a browser check so it can never fire during SSR.
 */
export const pinProtectionResponseInterceptor = (
  error: AxiosError,
): Promise<never> => {
  if (typeof window !== "undefined" && isPinProtectionError(error)) {
    showPinProtectionAlert();
  }
  return Promise.reject(error);
};
